import { Session } from 'next-auth';
import { SessionProvider, useSession } from 'next-auth/react';
import { PropsWithChildren, useEffect, useState } from 'react';
import usePageVisibility from '../../hooks/usePageVisibility';
import { SetStateType } from '../../model/types';

function deriveRefreshTimeInMinute(expires_in: number) {
    if (expires_in <= 1800) return 15;
    if (expires_in <= 3600) return 30;
    if (expires_in <= 36000) return 60;
    return 60;
}

/**
 * https://dev.to/mabaranowski/nextjs-authentication-jwt-refresh-token-rotation-with-nextauthjs-5696
 *
 * It sets the interval to the @timeRemaining before the token expires, minus 2 minutes (see [...nextauth].ts file)
 * @param  - `setInterval` is a function that will be called with the time remaining in seconds before the token expires
 */
function RefreshTokenHandler({ setInterval }: PropsWithChildren<{ setInterval: SetStateType<number> }>) {
    const { data: session } = useSession();

    const isTabVisible = usePageVisibility();
    useEffect(() => {
        if (!isTabVisible) {
            setInterval(0);
        } else if (session) {
            const tokenExpiry = session.expiresIn;
            // We did set the token to be ready to refresh after 2 minutes (see [...nextauth].ts file), here we set interval of 5 minutes.
            const timeRemaining = Math.round(
                tokenExpiry - deriveRefreshTimeInMinute(session.expires_in) * 60 - Date.now() / 1000
            );
            console.debug(
                `RefreshTokenHandler , timeRemaining`,
                timeRemaining,
                'ms',
                '=>',
                Math.floor(timeRemaining / 60),
                'min,',
                timeRemaining - Math.floor(timeRemaining / 60) * 60,
                'seconds'
            );

            setInterval(timeRemaining > 0 ? timeRemaining : 0);
        }
    }, [isTabVisible, session, setInterval]);

    return null;
}

export default function SessionController({ children, session }: PropsWithChildren<{ session: Session }>) {
    const [interval, setInterval] = useState(0);

    return (
        <SessionProvider session={session} refetchInterval={interval}>
            <RefreshTokenHandler setInterval={setInterval} />
            {children}
        </SessionProvider>
    );
}
